const API_BASE = '/api';

/**
 * Custom error class for API errors with status code
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

/**
 * Makes an authenticated API request
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(data.error || 'Request failed', response.status);
  }

  return data as T;
}

/**
 * Authentication API methods
 */
export const authApi = {
  login: (username: string, password: string) =>
    apiRequest<{ token: string; user: { id: string; username: string; permissions: string[] } }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ username, password })
      }
    ),

  register: (username: string, password: string) =>
    apiRequest<{ token: string; user: { id: string; username: string; permissions: string[] } }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ username, password })
      }
    ),

  verify: () =>
    apiRequest<{ valid: boolean; user: { id: string; username: string; permissions: string[] } }>(
      '/auth/verify',
      {
        method: 'POST'
      }
    ),

  getMe: () =>
    apiRequest<{ user: { id: string; username: string; permissions: string[] } }>('/auth/me')
};

/**
 * Maps API methods
 */
export interface MapMeta {
  id: string;
  label: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  backgroundMaterial?: string | null;
  backgroundRandomRotation?: boolean;
  /** Pixels per meter scale (default: 128 means 1m = 128px) */
  scale?: number;
  /** Folder path for organization (e.g., "World/Regions/Forest") */
  folder?: string;
  /** Whether this map is visible in view mode */
  isPublic?: boolean;
}

export const mapsApi = {
  list: () =>
    apiRequest<{ maps: MapMeta[] }>('/maps'),

  get: (id: string) =>
    apiRequest<{ meta: MapMeta }>(`/maps/${id}`),

  create: (id: string, label: string, scale?: number, folder?: string) =>
    apiRequest<{ meta: MapMeta }>(
      '/maps',
      {
        method: 'POST',
        body: JSON.stringify({ id, label, scale, folder })
      }
    ),

  delete: (id: string) =>
    apiRequest<{ message: string }>(
      `/maps/${id}`,
      {
        method: 'DELETE'
      }
    ),

  setBackground: (mapId: string, materialName: string | null) =>
    apiRequest<{ meta: MapMeta }>(
      `/maps/${mapId}/background`,
      {
        method: 'PUT',
        body: JSON.stringify({ materialName })
      }
    ),

  updateSettings: (mapId: string, settings: {
    label?: string;
    backgroundMaterial?: string | null;
    backgroundColor?: string | null;
    backgroundRandomRotation?: boolean;
    scale?: number;
    folder?: string | null;
    isPublic?: boolean;
  }) =>
    apiRequest<{ meta: MapMeta }>(
      `/maps/${mapId}/settings`,
      {
        method: 'PUT',
        body: JSON.stringify(settings)
      }
    )
};

/**
 * Assets API methods (global assets, not per-map)
 */
export const assetsApi = {
  list: (assetType: string) =>
    apiRequest<{ assets: { name: string; label: string; filename: string; createdAt: string }[] }>(
      `/assets/${assetType}`
    ),

  upload: async (assetType: string, file: File, name: string, label: string, defaultSizeMeters?: number) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('label', label);
    if (defaultSizeMeters !== undefined) {
      formData.append('defaultSizeMeters', defaultSizeMeters.toString());
    }

    const response = await fetch(`${API_BASE}/assets/${assetType}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.error || 'Upload failed', response.status);
    }

    return data as { asset: { name: string; label: string; filename: string; createdAt: string; defaultSizeMeters?: number } };
  },

  delete: (assetType: string, assetName: string) =>
    apiRequest<{ message: string }>(
      `/assets/${assetType}/${assetName}`,
      {
        method: 'DELETE'
      }
    ),

  update: (assetType: string, assetName: string, updates: { label?: string; defaultSizeMeters?: number; folder?: string | null }) =>
    apiRequest<{ asset: { name: string; label: string; filename: string; createdAt: string; defaultSizeMeters?: number; folder?: string | null } }>(
      `/assets/${assetType}/${assetName}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  getFileUrl: (assetType: string, assetName: string) =>
    `${API_BASE}/assets/${assetType}/${assetName}/file`
};

/**
 * Global settings API
 */
export interface GlobalSettings {
  defaultMapId: string | null;
}

export const settingsApi = {
  get: () =>
    apiRequest<{ settings: GlobalSettings }>('/settings'),

  setDefaultMap: (mapId: string | null) =>
    apiRequest<{ settings: GlobalSettings }>(
      '/settings/default-map',
      {
        method: 'PUT',
        body: JSON.stringify({ mapId })
      }
    )
};

/**
 * Props API methods
 */
export interface PropInstance {
  id: string;
  propName: string;
  x: number;
  y: number;
  scale: number;
  /** Scale X for non-uniform scaling (defaults to scale if not set) */
  scaleX?: number;
  /** Scale Y for non-uniform scaling (defaults to scale if not set) */
  scaleY?: number;
  rotation: number;
  zIndex: number;
  /** Layer this prop belongs to (defaults to 'default') */
  layerId?: string;
  regionX?: number;
  regionY?: number;
}

/**
 * Layer interface for organizing props
 */
export interface Layer {
  id: string;
  name: string;
  visible: boolean;
  order: number;
  /** Layer opacity from 0-100 (default: 100) */
  opacity: number;
  /** Parent layer ID for hierarchical structure (null = root level) */
  parentId: string | null;
  /** Layer description in markdown format */
  description: string;
}

/**
 * Tiles API methods (Leaflet-style z/x/y tile pyramid)
 */
export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

export const tilesApi = {
  /** Get tile URL for direct image loading */
  getTileUrl: (mapId: string, z: number, x: number, y: number) =>
    `${API_BASE}/maps/${mapId}/tiles/${z}/${x}/${y}`,

  /** List tiles at specific zoom level(s) */
  listTiles: (mapId: string, zoom?: number) =>
    apiRequest<{ tiles: Record<string, Array<{ x: number; y: number }>> }>(
      `/maps/${mapId}/tiles/list${zoom !== undefined ? `?zoom=${zoom}` : ''}`
    ),

  /** Get multiple tiles as base64 */
  getTilesBulk: (mapId: string, tiles: TileCoord[]) =>
    apiRequest<{ tiles: Record<string, string> }>(
      `/maps/${mapId}/tiles/bulk`,
      {
        method: 'POST',
        body: JSON.stringify({ tiles })
      }
    ),

  /** Save a single tile */
  saveTile: async (mapId: string, z: number, x: number, y: number, imageData: Blob): Promise<{ message: string }> => {
    const token = localStorage.getItem('token');

    const response = await fetch(
      `${API_BASE}/maps/${mapId}/tiles/${z}/${x}/${y}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/octet-stream'
        },
        body: imageData
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(data.error || 'Failed to save tile', response.status);
    }

    return data;
  },

  /** Save multiple tiles at once */
  saveTilesBatch: (mapId: string, tiles: Array<{ z: number; x: number; y: number; data: string }>) =>
    apiRequest<{ message: string; saved: number; deleted: number }>(
      `/maps/${mapId}/tiles/batch`,
      {
        method: 'POST',
        body: JSON.stringify({ tiles })
      }
    ),

  /** Regenerate pyramid from affected tiles */
  regeneratePyramid: (mapId: string, fromZoom: number, tiles: Array<{ x: number; y: number }>) =>
    apiRequest<{ message: string; regeneratedCount: number }>(
      `/maps/${mapId}/tiles/regenerate-pyramid`,
      {
        method: 'POST',
        body: JSON.stringify({ fromZoom, tiles })
      }
    )
};

/**
 * Props API methods
 */
export const propsApi = {
  /** Get all props for a map */
  list: (mapId: string) =>
    apiRequest<{ props: PropInstance[] }>(`/maps/${mapId}/props`),

  /** Get props for a specific region */
  getRegionProps: (mapId: string, regionX: number, regionY: number) =>
    apiRequest<{ regionX: number; regionY: number; props: PropInstance[] }>(
      `/maps/${mapId}/props/region/${regionX}/${regionY}`
    ),

  /** Create a new prop */
  create: (mapId: string, prop: {
    propName: string;
    x: number;
    y: number;
    scale?: number;
    rotation?: number;
    zIndex?: number;
    layerId?: string;
  }) =>
    apiRequest<{ prop: PropInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/props`,
      {
        method: 'POST',
        body: JSON.stringify(prop)
      }
    ),

  /** Update a prop */
  update: (mapId: string, propId: string, updates: Partial<PropInstance>) =>
    apiRequest<{ prop: PropInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/props/${propId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  /** Delete a prop */
  delete: (mapId: string, propId: string) =>
    apiRequest<{ message: string }>(
      `/maps/${mapId}/props/${propId}`,
      {
        method: 'DELETE'
      }
    ),

  /** Change prop layer (z-index) */
  changeLayer: (mapId: string, propId: string, direction: 'up' | 'down') =>
    apiRequest<{ prop: PropInstance }>(
      `/maps/${mapId}/props/${propId}/layer`,
      {
        method: 'PUT',
        body: JSON.stringify({ direction })
      }
    ),

  /** Save multiple props at once */
  saveBatch: (mapId: string, props: PropInstance[]) =>
    apiRequest<{ message: string; count: number }>(
      `/maps/${mapId}/props/batch`,
      {
        method: 'POST',
        body: JSON.stringify({ props })
      }
    )
};

/**
 * Layers API methods
 */
/**
 * Text alignment options
 */
export type TextAlignment = 'left' | 'center' | 'right';

/**
 * Text instance on the map
 */
export interface TextInstance {
  id: string;
  /** The text content */
  content: string;
  /** X position in world coordinates */
  x: number;
  /** Y position in world coordinates */
  y: number;
  /** Width of the text box */
  width: number;
  /** Height of the text box */
  height: number;
  /** Rotation in degrees */
  rotation: number;
  /** Font family name */
  fontFamily: string;
  /** Font size in pixels */
  fontSize: number;
  /** Font weight (100-900 or 'normal', 'bold') */
  fontWeight: number | string;
  /** Text color (hex or rgba) */
  color: string;
  /** Text alignment */
  alignment: TextAlignment;
  /** Stroke color (null for no stroke) */
  strokeColor: string | null;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Z-index for rendering order */
  zIndex: number;
  /** Layer this text belongs to */
  layerId: string;
  /** Region X coordinate */
  regionX?: number;
  /** Region Y coordinate */
  regionY?: number;
  /** If true, text size stays fixed on screen regardless of zoom */
  fixedSize?: boolean;
}

/**
 * Texts API methods
 */
export const textsApi = {
  /** Get all texts for a map */
  list: (mapId: string) =>
    apiRequest<{ texts: TextInstance[] }>(`/maps/${mapId}/texts`),

  /** Get texts for a specific region */
  getRegionTexts: (mapId: string, regionX: number, regionY: number) =>
    apiRequest<{ regionX: number; regionY: number; texts: TextInstance[] }>(
      `/maps/${mapId}/texts/region/${regionX}/${regionY}`
    ),

  /** Create a new text */
  create: (mapId: string, text: Omit<TextInstance, 'id' | 'regionX' | 'regionY'>) =>
    apiRequest<{ text: TextInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/texts`,
      {
        method: 'POST',
        body: JSON.stringify(text)
      }
    ),

  /** Update a text */
  update: (mapId: string, textId: string, updates: Partial<TextInstance>) =>
    apiRequest<{ text: TextInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/texts/${textId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  /** Delete a text */
  delete: (mapId: string, textId: string) =>
    apiRequest<{ message: string }>(
      `/maps/${mapId}/texts/${textId}`,
      {
        method: 'DELETE'
      }
    )
};

/**
 * Pen path instance on the map
 */
export interface PenPathInstance {
  id: string;
  /** Array of anchor points defining the path */
  points: Array<{
    x: number;
    y: number;
    handleIn: { x: number; y: number } | null;
    handleOut: { x: number; y: number } | null;
    handleMode: 'smooth' | 'corner';
  }>;
  /** Whether the path is closed */
  closed: boolean;
  /** Stroke color (null for no stroke) */
  strokeColor: string | null;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Stroke opacity (0-1) */
  strokeOpacity: number;
  /** Line cap style */
  lineCap: 'butt' | 'round' | 'square';
  /** Line join style */
  lineJoin: 'miter' | 'round' | 'bevel';
  /** Dash pattern preset */
  dashPreset: 'solid' | 'dashed' | 'dotted';
  /** Arrow type at start */
  arrowStart: 'none' | 'arrow' | 'triangle' | 'circle' | 'square' | 'diamond';
  /** Arrow type at end */
  arrowEnd: 'none' | 'arrow' | 'triangle' | 'circle' | 'square' | 'diamond';
  /** Fill color (null for no fill) */
  fillColor: string | null;
  /** Fill opacity (0-1) */
  fillOpacity: number;
  /** X position of path center */
  x: number;
  /** Y position of path center */
  y: number;
  /** Rotation in degrees */
  rotation: number;
  /** Scale factor */
  scale: number;
  /** Z-index for rendering order */
  zIndex: number;
  /** Layer this path belongs to */
  layerId: string;
  /** Region X coordinate */
  regionX?: number;
  /** Region Y coordinate */
  regionY?: number;
  /** Feather radius for soft edges (0 for no feather) */
  featherRadius: number;
  /** Invert fill - fills everything except the path (cutout effect) */
  invertFill: boolean;
}

/**
 * Pen Paths API methods
 */
export const penPathsApi = {
  /** Get all pen paths for a map */
  list: (mapId: string) =>
    apiRequest<{ penPaths: PenPathInstance[] }>(`/maps/${mapId}/penpaths`),

  /** Get pen paths for a specific region */
  getRegionPaths: (mapId: string, regionX: number, regionY: number) =>
    apiRequest<{ regionX: number; regionY: number; penPaths: PenPathInstance[] }>(
      `/maps/${mapId}/penpaths/region/${regionX}/${regionY}`
    ),

  /** Create a new pen path */
  create: (mapId: string, penPath: Omit<PenPathInstance, 'id' | 'regionX' | 'regionY'>) =>
    apiRequest<{ penPath: PenPathInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/penpaths`,
      {
        method: 'POST',
        body: JSON.stringify(penPath)
      }
    ),

  /** Update a pen path */
  update: (mapId: string, pathId: string, updates: Partial<PenPathInstance>) =>
    apiRequest<{ penPath: PenPathInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/penpaths/${pathId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  /** Delete a pen path */
  delete: (mapId: string, pathId: string) =>
    apiRequest<{ message: string }>(
      `/maps/${mapId}/penpaths/${pathId}`,
      {
        method: 'DELETE'
      }
    )
};

// Re-export ShapeInstance from shape.ts for consistency
import { ShapeInstance, ShapeParams, ShapeType } from '../types/shape';
export type { ShapeInstance, ShapeParams, ShapeType };

/**
 * Shapes API methods
 */
export const shapesApi = {
  /** Get all shapes for a map */
  list: (mapId: string) =>
    apiRequest<{ shapes: ShapeInstance[] }>(`/maps/${mapId}/shapes`),

  /** Get shapes for a specific region */
  getRegionShapes: (mapId: string, regionX: number, regionY: number) =>
    apiRequest<{ regionX: number; regionY: number; shapes: ShapeInstance[] }>(
      `/maps/${mapId}/shapes/region/${regionX}/${regionY}`
    ),

  /** Create a new shape */
  create: (mapId: string, shape: Omit<ShapeInstance, 'id' | 'regionX' | 'regionY'>) =>
    apiRequest<{ shape: ShapeInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/shapes`,
      {
        method: 'POST',
        body: JSON.stringify(shape)
      }
    ),

  /** Update a shape */
  update: (mapId: string, shapeId: string, updates: Partial<ShapeInstance>) =>
    apiRequest<{ shape: ShapeInstance; regionX: number; regionY: number }>(
      `/maps/${mapId}/shapes/${shapeId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  /** Delete a shape */
  delete: (mapId: string, shapeId: string) =>
    apiRequest<{ message: string }>(
      `/maps/${mapId}/shapes/${shapeId}`,
      {
        method: 'DELETE'
      }
    )
};

export const layersApi = {
  /** Get all layers for a map */
  list: (mapId: string) =>
    apiRequest<{ layers: Layer[] }>(`/maps/${mapId}/layers`),

  /** Create a new layer */
  create: (mapId: string, name: string, parentId?: string | null) =>
    apiRequest<{ layer: Layer }>(
      `/maps/${mapId}/layers`,
      {
        method: 'POST',
        body: JSON.stringify({ name, parentId })
      }
    ),

  /** Update a layer */
  update: (mapId: string, layerId: string, updates: Partial<Pick<Layer, 'name' | 'order' | 'parentId' | 'description'>>) =>
    apiRequest<{ layer: Layer }>(
      `/maps/${mapId}/layers/${layerId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates)
      }
    ),

  /** Reorder all layers */
  reorder: (mapId: string, layerIds: string[]) =>
    apiRequest<{ layers: Layer[] }>(
      `/maps/${mapId}/layers/reorder`,
      {
        method: 'PUT',
        body: JSON.stringify({ layerIds })
      }
    ),

  /** Delete a layer */
  delete: (mapId: string, layerId: string, movePropsTo?: string) =>
    apiRequest<{ message: string; movePropsTo: string | null }>(
      `/maps/${mapId}/layers/${layerId}${movePropsTo ? `?movePropsTo=${movePropsTo}` : ''}`,
      {
        method: 'DELETE'
      }
    )
};

export default apiRequest;
